import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import Link from "../../Link"
import Typography, { TypographySize } from "../Typography"

import {
  SharedButtonClickProps,
  handleOnClick,
} from "@utils/buttonEventHandler"
import { getTextColorClass, getTextHoverColorClass } from "@utils/V2/color"
import React, { ReactNode, useMemo } from "react"

type TextButtonProps = {
  text: string
  highlightedText?: string
  highlightWeight?: "book" | "medium"
  mobileText?: string
  textSize?: TypographySize
  style:
    | "icon"
    | "underlined"
    | "underlined-static"
    | "text"
    | "underlined-reverse"
    | "none"
  weight?: "book" | "medium"
  color: Color
  hoverColor?: Color
  linkUrl?: string | YocoCom.YocoLink
  opensNewTab?: boolean
  queryParams?: string
  mobileLinkUrl?: string | YocoCom.YocoLink
  icon?: ReactNode
  disabled?: boolean
  onClick?: () => void
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
  className?: string
  uppercase?: boolean
  scrollOffset?: number
  "data-testid"?: string
  decorative?: boolean
}

const TextButton = ({
  text,
  highlightedText,
  highlightWeight,
  icon,
  color,
  hoverColor,
  style,
  weight = "medium",
  onClick,
  linkUrl,
  opensNewTab = false,
  queryParams,
  mobileText,
  mobileLinkUrl,
  textSize = style === "text" || style === "underlined-reverse"
    ? "subscript-lg"
    : "body-lg",
  trackingEvent,
  trackingEventKey,
  disabled = false,
  trackingEventValue,
  uppercase = style === "text",
  className,
  scrollOffset,
  "data-testid": dataTestId,
  decorative = false,
}: TextButtonProps) => {
  const desktopLinkUrl = typeof linkUrl === "string" ? linkUrl : linkUrl?.url
  const mobileResolvedLinkUrl =
    typeof mobileLinkUrl === "string" ? mobileLinkUrl : mobileLinkUrl?.url

  const sharedButtonClickProps: SharedButtonClickProps = {
    trackingEvent,
    trackingEventKey,
    trackingEventValue,
    scrollOffset,
    onClick,
  }

  const hoverColorClass = hoverColor
    ? getTextHoverColorClass(hoverColor)
    : undefined

  const buttonClasses = useMemo(
    () =>
      classNames(
        "group inline-flex py-4 max-w-fit",
        getTextColorClass(color),
        hoverColorClass,
        { "opacity-50 pointer-events-none": disabled },
        { "items-center gap-8": style === "icon" },
        {
          "gap-[2px] flex-col after:content-[''] after:block after:bg-current after:h-px after:transition-[width] after:duration-300":
            style === "underlined" ||
            style === "underlined-static" ||
            style === "text" ||
            style === "underlined-reverse",
        },
        {
          "after:w-full after:hover:w-[80%] after:focus-visible:w-[80%] items-center":
            style === "underlined",
        },
        {
          "after:w-full items-center": style === "underlined-static",
        },
        {
          "after:w-full after:hover:w-0 after:focus-visible:w-0 items-center":
            style === "underlined-reverse",
        },
        {
          "after:w-0 after:hover:w-full after:focus-visible:w-full items-start":
            style === "text",
        },
        { uppercase: uppercase },
        className
      ),
    [color, hoverColorClass, disabled, style, uppercase, className]
  )

  const label = (
    <>
      {mobileText && (
        <Typography
          text={mobileText}
          weight={weight}
          font="grotesk"
          color={color}
          size={textSize}
          leadingNone
          className={classNames("sm:hidden", hoverColorClass)}
        />
      )}
      <Typography
        text={text}
        highlightedText={highlightedText}
        highlightWeight={highlightWeight}
        highlightColor={color}
        highlightClassName={classNames("!leading-none", hoverColorClass)}
        weight={weight}
        font="grotesk"
        color={color}
        size={textSize}
        leadingNone
        className={classNames(
          { "hidden sm:block": mobileText },
          hoverColorClass
        )}
      />
    </>
  )

  const buttonContent = (
    <>
      {icon && style !== "icon" ? (
        <span className="flex items-center gap-4">
          {label}
          <span className={classNames("shrink-0", getTextColorClass(color))}>
            {icon}
          </span>
        </span>
      ) : (
        label
      )}

      {style === "icon" && icon && (
        <div
          className={classNames(
            getTextColorClass(color),
            "w-20 md:w-[22px] lg:w-24"
          )}
        >
          {icon}
        </div>
      )}
    </>
  )

  if (decorative) {
    return <span className={buttonClasses}>{buttonContent}</span>
  }

  if (linkUrl || mobileLinkUrl) {
    return (
      <>
        {linkUrl && (
          <Link
            link={linkUrl}
            opensNewTab={opensNewTab}
            onClick={(e) =>
              handleOnClick({
                e,
                linkUrl: desktopLinkUrl,
                ...sharedButtonClickProps,
              })
            }
            className={classNames(buttonClasses, {
              "hidden sm:inline-flex": mobileLinkUrl,
            })}
            queryParams={queryParams}
            data-testid={`${dataTestId}-desktop`}
          >
            {buttonContent}
          </Link>
        )}
        {mobileLinkUrl && (
          <Link
            link={mobileLinkUrl}
            opensNewTab={opensNewTab}
            onClick={(e) =>
              handleOnClick({
                e,
                linkUrl: mobileResolvedLinkUrl,
                ...sharedButtonClickProps,
              })
            }
            className={classNames(buttonClasses, { "sm:hidden": linkUrl })}
            queryParams={queryParams}
            data-testid={`${dataTestId}-mobile`}
          >
            {buttonContent}
          </Link>
        )}
      </>
    )
  }

  return (
    <button
      type="button"
      onClick={(e) => handleOnClick({ e, ...sharedButtonClickProps })}
      className={buttonClasses}
      disabled={disabled}
      data-testid={dataTestId}
    >
      {buttonContent}
    </button>
  )
}

export default TextButton
